<template>
    <v-dialog
        v-model="payAllModal"
        fullscreen
        persistent
        hide-overlay
        transition="dialog-bottom-transition"
        :content-class="twilioChat ? 'twilioDrawer' : ''"
    >
        <template v-slot:activator="{ on }">
          <span small v-on="on">
              <v-btn
                  data-testid="take-payment-btn"
                  class="take-payment-btn success ml-4"
                  outlined
                  @click.stop="openPayAllModal"
              >
                  <v-icon class="mr-3">credit_card</v-icon>
                  Take Payment
              </v-btn>
          </span>
        </template>
        <v-card>
            <v-card-title class="text-h5" primary-title>
                <transition name="scroll-y-transition" mode="out-in">
                    <span v-if="recording" key="pay">Take Payment</span>
                    <span v-else class="error--text" key="recording">
                        Please complete credit card retrieval. You must complete retrieval before performing other
                        actions.
                    </span>
                </transition>
            </v-card-title>
            <v-divider />
            <v-card-text>
                <v-form ref="payAllForm">
                    <v-stepper v-model="currentStep">
                        <v-stepper-header>
                            <v-stepper-step
                                :color="currentStep === 1 ? 'secondary' : 'success'"
                                :complete="currentStep > 1"
                                step="1"
                                :class="currentStep === 1 ? 'title' : 'subheading'"
                            >
                              Unit Select
                            </v-stepper-step>

                            <v-divider :class="currentStep > 1 ? 'success' : null" />

                            <v-stepper-step
                                :color="currentStep <= 2 ? 'secondary' : 'success'"
                                :complete="currentStep > 2"
                                step="2"
                                :class="currentStep === 2 ? 'title' : 'subheading'"
                            >
                              Payment Options
                            </v-stepper-step>

                            <v-divider :class="currentStep > 2 ? 'success' : null" />

                            <v-stepper-step
                                :color="currentStep <= 3 ? 'secondary' : 'success'"
                                :complete="currentStep > 3"
                                step="3"
                                :class="currentStep === 3 ? 'title' : 'subheading'"
                            >
                              Payment Review
                            </v-stepper-step>

                            <v-divider :class="currentStep > 3 ? 'success' : null" />

                            <v-stepper-step
                                color="success"
                                :complete="currentStep === 4"
                                step="4"
                                :class="currentStep === 4 ? 'title' : 'subheading'"
                            >
                              Payment Success
                            </v-stepper-step>
                        </v-stepper-header>
                        <v-stepper-items>
                            <v-layout>
                                <v-flex :xs8="currentStep < 3">
                                    <v-stepper-content step="1">
                                        <unit-select />
                                    </v-stepper-content>
                                    <v-stepper-content step="2">
                                        <payment-options
                                            :recording="recording"
                                            @unitSelect="currentStep = 1"
                                            @reset="takePayment = false"
                                        />
                                    </v-stepper-content>
                                    <v-stepper-content step="3">
<!--                                        @TODO: Uncomment when convenience fees are reactivated.-->
<!--                                        <payment-review-->
<!--                                            :currentStep="currentStep"-->
<!--                                            :cart="cart"-->
<!--                                            :convenienceFees="convenienceFees"-->
<!--                                            @unitSelect="currentStep = 1"-->
<!--                                            @paymentOptions="currentStep = 2"-->
<!--                                        />-->
                                        <payment-review
                                            :currentStep="currentStep"
                                            :cart="cart"
                                            @unitSelect="currentStep = 1"
                                            @paymentOptions="currentStep = 2"
                                        />
                                    </v-stepper-content>
                                    <v-stepper-content step="4">
<!--                                        @TODO: Uncomment when convenience fees are reactivated.-->
<!--                                        <payment-success-->
<!--                                            :cart="cart"-->
<!--                                            :convenienceFees="convenienceFees"-->
<!--                                        />-->
                                        <payment-success :cart="cart" />
                                    </v-stepper-content>
                                </v-flex>
                                <v-flex
                                    v-if="currentStep < 3"
                                    xs4
                                    class="mr-4 mt-4"
                                >
<!--                                    <side-total-->
<!--                                        :feeLoading="feeLoading"-->
<!--                                        :convenienceFees="convenienceFees"-->
<!--                                        :convenienceFeeStatus="convenienceFeeStatus"-->
<!--                                        :convenienceWaived="convenienceWaived"-->
<!--                                        @waiveConvenienceFee="waiveConvenienceFee"-->
<!--                                    ></side-total>-->
<!--                                        @TODO: Uncomment when convenience fees are reactivated.-->
                                    <side-total :feeLoading="feeLoading" />
                                </v-flex>
                            </v-layout>
                        </v-stepper-items>
                    </v-stepper>
                </v-form>
            </v-card-text>
            <v-divider />
            <v-card-actions>
                <v-layout v-if="currentStep === 4">
                    <v-btn
                        data-testid="payment-dismiss-btn"
                        class="payment-dismiss-btn mt-2 ml-3"
                        color="error"
                        large
                        @click="collapseAfterSuccess"
                    >
                      Dismiss
                    </v-btn>
                    <v-spacer />
                    <disposition
                      :dispositionOpen="dispositionOpen"
                      @collapse="dispositionOpen = false"
                    >
                        <v-btn
                            data-testid="payment-disposition-btn"
                            class="payment-disposition-btn mt-2 mr-3"
                            color="primary"
                            large
                            @click.stop="dispositionOpen = true"
                        >
                            Disposition
                        </v-btn>
                    </disposition>
                </v-layout>
                <v-layout v-else>
                    <v-btn
                        data-testid="payment-cancel-btn"
                        class="payment-cancel-btn mt-2 ml-3"
                        color="error"
                        @click="collapse"
                        large
                        :disabled="!recording"
                    >
                      Cancel
                    </v-btn>
                    <v-spacer />
                    <div>
                        <v-btn
                            data-testid="payment-take-payment-btn"
                            class="payment-take-payment-btn mt-2 mr-3"
                            v-show="currentStep === 1"
                            :disabled="!cart.length"
                            :loading="loading"
                            color="primary"
                            large
                            @click="toPayment"
                        >
                            Take Payment
                        </v-btn>
                        <v-btn
                            data-testid="payment-view-summary-btn"
                            class="payment-view-summary-btn mt-2 mr-3"
                            v-show="currentStep === 2"
                            :disabled="!canSubmit || !recording"
                            :loading="paymentLoading"
                            color="primary"
                            large
                            @click="toSummary"
                        >
                            View Summary
                        </v-btn>
                        <v-btn
                            data-testid="payment-submit-btn"
                            class="payment-submit-btn mt-2 mr-3"
                            v-show="currentStep === 3"
                            :loading="paymentLoading"
                            color="success"
                            large
                            @click="sendPayment"
                        >
                            Submit
                        </v-btn>
                    </div>
                </v-layout>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
/* eslint-disable no-return-assign */
import Disposition from "@/components/Disposition";
import PaymentOptions from "@/components/tenant-rentals/actions/TakePayment/steps/PaymentOptions";
import PaymentReview from "@/components/tenant-rentals/actions/TakePayment/steps/PaymentReview";
import PaymentSuccess from "./steps/PaymentSuccess";
import SideTotal from "@/components/tenant-rentals/actions/TakePayment/partials/SideTotal";
import UnitSelect from "@/components/tenant-rentals/actions/TakePayment/steps/UnitSelect";
import { mapState } from "vuex";

export default {
  name: "TakePayment",
  props: ["convenienceFeeStatus"],
  data() {
    return {
      payAllModal: false,
      loading: false,
      paymentLoading: false,
      dispositionOpen: false,
      currentStep: 1,
      takePayment: false,
      // convenienceWaived: false, @TODO: Uncomment when convenience fees are reactivated.
      feeLoading: false,
      // convenienceFees: "", @TODO: Uncomment when convenience fees are reactivated.
      disableWaive: false
    };
  },
  methods: {
    toPayment() {
      this.currentStep = 2;
    },

    toSummary() {
      this.currentStep = 3;
    },

    openPayAllModal() {
      this.takePayment = false;
      this.payAllModal = true;
      // this.getConvenienceFee(); @TODO: Uncomment when convenience fees are reactivated.
    },

    collapse() {
      this.$store.commit("clearCreditCard");
      this.payAllModal = false;
      this.$refs.payAllForm.reset();
      this.$store.commit("updatePaymentValue", { key: "cart", value: [] });
      this.currentStep = 1;
    },

    collapseAfterSuccess() {
      this.payAllModal = false;
      this.$emit("success");
      this.collapse();
    },

    // getConvenienceFee() { @TODO: Uncomment when convenience fees are reactivated.
    //     this.feeLoading = true;
    //     this.$store.dispatch("getConvenienceFee", this.$route.params.facilityId)
    //         .then(resp => {
    //             this.convenienceFees = resp.data.fees[0];
    //             this.feeLoading = false;
    //         })
    //         .catch(err => {
    //             this.$store.dispatch("handleError", err);
    //             this.feeLoading = false;
    //         });
    // },
    //
    // waiveConvenienceFee() { @TODO: Uncomment when convenience fees are reactivated.
    //     let payload = { facilityId: this.$route.params.facilityId, tenantId: this.$route.params.tenantId };
    //     this.$store.dispatch("waiveConvenienceFee", payload)
    //         .then(resp => {
    //             this.convenienceFees = {
    //                 dcPrice: 0,
    //                 dcTax1Rate: 0,
    //                 dcTax2Rate: 0,
    //                 dcTotal: 0,
    //             };
    //             this.convenienceWaived = true;
    //             this.disableWaive = true;
    //         })
    //         .catch(err => {
    //             this.$store.dispatch("handleError", err);
    //             this.disableWaive = true;
    //         });
    // },

    formatCC(ccNumber) {
      if (ccNumber && typeof ccNumber.replace === "function") {
        return ccNumber.replace(new RegExp(" ", "g"), "-");
      }
    },

    sendPayment() {
      this.paymentLoading = true;
      this.$store.dispatch("makePayment", this.payload)
        .then(resp => {
          this.$store.dispatch("handleSuccess", "Payment processed successfully!");
          this.paymentLoading = false;
          this.currentStep = 4;
        })
        .catch(err => {
          this.$store.dispatch("handleError", err);
          this.paymentLoading = false;
        });
    }

  },
  computed: {
    ...mapState({
      cart: state => state.tenant.payment.cart,
      creditCard: state => state.tenant.creditCard,
      darkMode: state => state.application.settings.darkTheme,
      recording: state => state.call.recording,
      twilioChat: state => state.application.twilioChat
    }),

    canSubmit() {
      return (
        this.currentStep === 2 &&
        this.creditCard.firstName &&
        this.creditCard.lastName &&
        this.creditCard.billingAddress &&
        (this.creditCard.billingZip && this.creditCard.billingZip.length === 5) &&
        this.creditCard.ccType &&
        (!!this.creditCard.ccNumber && (this.creditCard.ccNumber.length >= 17 && this.creditCard.ccNumber.length <= 19)) &&
        this.creditCard.ccExpirationMonth &&
        this.creditCard.ccExpirationYear &&
        (!!this.creditCard.ccCVV && (this.creditCard.ccCVV.length >= 3 && this.creditCard.ccCVV.length <= 4))
      );
    },

    payload() {
      const ledgers = [];
      let total_payment_amount = 0;

      this.cart.forEach(ledger => {
        ledgers.push({
          external_id: ledger.ledger_id,
          prepaid_months: ledger.prepaid_months,
          external_unit_id: ledger.unit_id,
          payment_amount: ledger.total
        });
        total_payment_amount += ledger.total;
      });

      return {
        ledgers,
        payment_method: "credit_card",
        total_payment_amount,
        billing_first_name: this.creditCard.firstName,
        billing_last_name: this.creditCard.lastName,
        cc_type: this.creditCard.ccType,
        cc_number: this.formatCC(this.creditCard.ccNumber),
        cc_exp_month: this.creditCard.ccExpirationMonth,
        cc_exp_year: String(this.creditCard.ccExpirationYear),
        cc_cvv: this.creditCard.ccCVV,
        facilityId: this.$route.params.facilityId,
        tenantId: this.$route.params.tenantId
      };
    }
  },
  created() {
    this.$store.dispatch("getAllGlobalSettings");
  },
  components: {
    UnitSelect,
    PaymentOptions,
    PaymentSuccess,
    PaymentReview,
    Disposition,
    SideTotal
  }
};
</script>
